import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [discount, setDiscount] = useState(() => {
    try {
      const saved = localStorage.getItem('cartDiscount');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  
  const [cartItems, setCartItems] = useState(() => {
    // Initialize from localStorage immediately
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        console.log('📦 Initial cart load:', parsed);
        
        // Apply limits to loaded cart items
        const MAX_QUANTITY_PER_PRODUCT = 3;
        const MAX_CART_ITEMS = 10;
        
        let correctedCart = parsed.map(item => ({
          ...item,
          quantity: Math.min(item.quantity, MAX_QUANTITY_PER_PRODUCT)
        }));
        
        // Check total cart limit
        let totalQuantity = correctedCart.reduce((sum, item) => sum + item.quantity, 0);
        
        if (totalQuantity > MAX_CART_ITEMS) {
          console.log(`⚠️ Cart total (${totalQuantity}) exceeds limit (${MAX_CART_ITEMS}), trimming...`);
          // Trim items to fit limit
          let remaining = MAX_CART_ITEMS;
          correctedCart = correctedCart.map(item => {
            if (remaining <= 0) return {...item, quantity: 0};
            const allowedQty = Math.min(item.quantity, remaining);
            remaining -= allowedQty;
            return {...item, quantity: allowedQty};
          }).filter(item => item.quantity > 0);
        }
        
        // Check if any corrections were made
        const hadCorrections = correctedCart.length !== parsed.length || 
                              correctedCart.some((item, idx) => item.quantity !== parsed[idx]?.quantity);
        if (hadCorrections) {
          console.log('✅ Cart corrected to comply with limits');
        }
        
        return correctedCart;
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
    return [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    console.log('💾 Saving cart to localStorage:', cartItems);
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    const MAX_CART_ITEMS = 10;
    const MAX_QUANTITY_PER_PRODUCT = 3;
    
    console.log('🛒 addToCart called with:', { product, quantity });
    
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      
      // Calculate current total quantity in cart
      const currentTotal = prevItems.reduce((sum, item) => sum + item.quantity, 0);
      
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        
        // Check per-product limit
        if (newQuantity > MAX_QUANTITY_PER_PRODUCT) {
          alert(`Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet ekleyebilirsiniz!`);
          console.log('❌ Per-product limit exceeded');
          return prevItems;
        }
        
        // Check total cart limit
        if (currentTotal + quantity > MAX_CART_ITEMS) {
          alert(`Sepetinizde maksimum ${MAX_CART_ITEMS} ürün olabilir!`);
          console.log('❌ Cart total limit exceeded');
          return prevItems;
        }
        
        console.log('✅ Product exists, updating quantity');
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      
      // Check per-product limit for new item
      if (quantity > MAX_QUANTITY_PER_PRODUCT) {
        alert(`Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet ekleyebilirsiniz!`);
        console.log('❌ Per-product limit exceeded for new item');
        return prevItems;
      }
      
      // Check total cart limit for new item
      if (currentTotal + quantity > MAX_CART_ITEMS) {
        alert(`Sepetinizde maksimum ${MAX_CART_ITEMS} ürün olabilir! Şu anda ${currentTotal} ürün var.`);
        console.log('❌ Cart total limit exceeded for new item');
        return prevItems;
      }
      
      console.log('✅ Adding new product to cart');
      return [...prevItems, { ...product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    const MAX_QUANTITY_PER_PRODUCT = 3;
    const MAX_CART_ITEMS = 10;
    
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    // Enforce per-product limit
    if (quantity > MAX_QUANTITY_PER_PRODUCT) {
      console.log(`❌ updateQuantity blocked: ${quantity} > ${MAX_QUANTITY_PER_PRODUCT}`);
      alert(`Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet sipariş verebilirsiniz!`);
      return;
    }
    
    setCartItems((prevItems) => {
      // Calculate new total
      const currentItem = prevItems.find(item => item.id === productId);
      if (!currentItem) return prevItems;
      
      const difference = quantity - currentItem.quantity;
      const currentTotal = prevItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotal = currentTotal + difference;
      
      // Enforce total cart limit
      if (newTotal > MAX_CART_ITEMS) {
        console.log(`❌ updateQuantity blocked: total would be ${newTotal} > ${MAX_CART_ITEMS}`);
        alert(`Sepetinizde maksimum ${MAX_CART_ITEMS} ürün olabilir!`);
        return prevItems;
      }
      
      return prevItems.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Discount helpers
  const applyDiscountCode = async (code, email = '') => {
    const subtotal = getCartTotal();
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, cart_total: subtotal, email })
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, message: data.detail || 'Kod geçersiz' };
      }
      const d = {
        code: data.code,
        percent: data.percent,
        amount: data.discount_amount
      };
      setDiscount(d);
      localStorage.setItem('cartDiscount', JSON.stringify(d));
      return { success: true, message: `${data.percent}% indirim uygulandı!`, discount: d };
    } catch (err) {
      return { success: false, message: 'Bir hata oluştu' };
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    localStorage.removeItem('cartDiscount');
  };

  const getDiscountAmount = () => {
    if (!discount) return 0;
    return Math.round((getCartTotal() * discount.percent / 100) * 100) / 100;
  };

  const getFinalTotal = () => {
    return getCartTotal() - getDiscountAmount();
  };

  // Clear discount when cart is emptied
  const clearCartAndDiscount = () => {
    setCartItems([]);
    removeDiscount();
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart: clearCartAndDiscount,
        getCartTotal,
        getCartCount,
        discount,
        applyDiscountCode,
        removeDiscount,
        getDiscountAmount,
        getFinalTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
